import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import useModal from '../../../../hooks/use-close-modal.hook';
import useRequest from '../../../../hooks/use-request.hook';
import {
  CPDService,
  type CpdOperatorUpsert,
  type CpdRouteOperatorUpsert,
  type CpdRouteUpsert
} from '../../../../services/cpd.service';
import { AlertService } from '../../../../services/alert.service';
import LogData from '../../../../components/logs/log-data.component';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import Button from '../../../../components/forms/button.component';
import Title from '../../../../components/forms/titles.component';
import Input from '../../../../components/forms/input.component';
import TexArea from '../../../../components/forms/text-area.component';
import Icon from '../../../../components/forms/icon.component';
import CpdOverridesFields, {
  EMPTY_OVERRIDES,
  countOverrides,
  fromApiOverrides,
  toApiOverrides,
  type OverridesFormValues
} from './cpd-overrides.fields';

export type CpdEntityKind = 'operator' | 'route' | 'route-operator';

interface Props {
  kind: CpdEntityKind;
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  /** Registro a editar (row de la tabla). null = crear. */
  payload: any | null;
  /** true = abrir con los datos del payload pero crear un registro nuevo. */
  duplicate?: boolean;
}

interface FormValues {
  operatorId: string;
  routeGroupId: string;
  carrier: string;
  description: string;
  enabled: boolean;
  templateId: string; // '' = null
  ov: OverridesFormValues;
}

const NO_TEMPLATE = { value: '', label: '— Sin template (usa default) —' };

const KIND_META: Record<
  CpdEntityKind,
  {
    title: string;
    titleNew: string;
    entity: string;
    hasOperator: boolean;
    hasRoute: boolean;
    hasCarrier: boolean;
  }
> = {
  operator: {
    title: 'Editar Operador',
    titleNew: 'Nuevo Operador',
    entity: 'operador',
    hasOperator: true,
    hasRoute: false,
    hasCarrier: false
  },
  route: {
    title: 'Editar Ruta',
    titleNew: 'Nueva Ruta',
    entity: 'ruta',
    hasOperator: false,
    hasRoute: true,
    hasCarrier: true
  },
  'route-operator': {
    title: 'Editar Override (operador × ruta)',
    titleNew: 'Nuevo Override (operador × ruta)',
    entity: 'override',
    hasOperator: true,
    hasRoute: true,
    hasCarrier: false
  }
};

const EMPTY: FormValues = {
  operatorId: '',
  routeGroupId: '',
  carrier: '',
  description: '',
  enabled: true,
  templateId: '',
  ov: EMPTY_OVERRIDES
};

function fromPayload(p: any): FormValues {
  return {
    operatorId: p?.operatorId && p.operatorId !== '—' ? String(p.operatorId) : '',
    routeGroupId:
      p?.routeGroupId !== undefined && p?.routeGroupId !== null && p.routeGroupId !== '—'
        ? String(p.routeGroupId)
        : '',
    carrier: p?.carrierRaw ?? '',
    description: p?.description ?? '',
    enabled: p?.enabled ?? true,
    templateId: p?.templateId && p.templateId !== '—' ? String(p.templateId) : '',
    ov: fromApiOverrides(p?.overrides)
  };
}

export default function CpdEntityModal({
  kind,
  show,
  onRequestClose,
  payload,
  duplicate = false
}: Props) {
  const { t } = useTranslation();
  const { call } = useRequest();
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);

  const meta = KIND_META[kind];
  const isEdit = payload != null && !duplicate;
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid, isSubmitting }
  } = useForm<FormValues>({ mode: 'onChange', defaultValues: EMPTY });

  const ov = watch('ov');
  const overridesCount = useMemo(() => countOverrides(ov ?? EMPTY_OVERRIDES), [ov]);

  useEffect(() => {
    reset(payload ? fromPayload(payload) : EMPTY);
  }, [payload, show, reset]);

  useEffect(() => {
    if (!show) return;
    let alive = true;
    setLoadingTemplates(true);
    CPDService.getTemplates()
      .then((res: any) => alive && setTemplates(Array.isArray(res) ? res : []))
      .catch(() => alive && setTemplates([]))
      .finally(() => alive && setLoadingTemplates(false));
    return () => {
      alive = false;
    };
  }, [show]);

  const templateOptions = useMemo(
    () => [
      NO_TEMPLATE,
      ...templates.map((tpl) => ({
        value: String(tpl.id ?? tpl._id),
        label: `${tpl.name}${tpl.config?.mode ? ` · ${tpl.config.mode}` : ''}${
          tpl.config?.toneCarrier ? ` · ${tpl.config.toneCarrier}` : ''
        }`
      }))
    ],
    [templates]
  );

  const cleanBootstrapBackdrop = () => {
    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };

  const closeModal = () => {
    onRequestClose(false);
    reset(EMPTY);
    cleanBootstrapBackdrop();
  };

  const buildBody = (f: FormValues) => {
    const common = {
      description: f.description.trim() || undefined,
      enabled: Boolean(f.enabled),
      templateId: f.templateId ? f.templateId : null,
      overrides: toApiOverrides(f.ov)
    };
    if (kind === 'operator') {
      const body: CpdOperatorUpsert = { operatorId: f.operatorId.trim(), ...common };
      return body;
    }
    if (kind === 'route') {
      const body: CpdRouteUpsert = {
        routeGroupId: Number(f.routeGroupId),
        carrier: f.carrier.trim() || undefined,
        ...common
      };
      return body;
    }
    const body: CpdRouteOperatorUpsert = {
      operatorId: f.operatorId.trim(),
      routeGroupId: Number(f.routeGroupId),
      ...common
    };
    return body;
  };

  const submit = async (formData: FormValues, buttonId: string) => {
    try {
      const label =
        kind === 'operator'
          ? formData.operatorId
          : kind === 'route'
            ? `grupo ${formData.routeGroupId}`
            : `${formData.operatorId} × grupo ${formData.routeGroupId}`;

      const isConfirmed = await AlertService.warning(
        t('general.atention'),
        isEdit
          ? `Estás por actualizar ${meta.entity} "${label}". Los FS lo tomarán en el próximo reload.`
          : `Estás por registrar ${meta.entity} "${label}".`,
        () => Promise.resolve(true)
      );
      if (!isConfirmed) return;

      const body = buildBody(formData);
      const id = String(payload?.id ?? payload?._id ?? '');

      if (kind === 'operator') {
        if (isEdit) await call(() => CPDService.updateOperator(id, body as CpdOperatorUpsert));
        else await call(() => CPDService.saveOperator(body as CpdOperatorUpsert));
      } else if (kind === 'route') {
        if (isEdit) await call(() => CPDService.updateRoute(id, body as CpdRouteUpsert));
        else await call(() => CPDService.saveRoute(body as CpdRouteUpsert));
      } else {
        if (isEdit) await call(() => CPDService.updateOverride(id, body as CpdRouteOperatorUpsert));
        else await call(() => CPDService.saveOverride(body as CpdRouteOperatorUpsert));
      }

      AlertService.toastSuccess(
        isEdit ? 'Registro actualizado exitosamente' : 'Registro creado exitosamente'
      );
      onRequestClose(true);
      reset(EMPTY);
      cleanBootstrapBackdrop();
      LogData(body, buttonId, window.location.href);
    } catch (error: any) {
      console.error(error);
      const msg = error?.error ?? error?.status ?? error;
      AlertService.toastError(
        typeof msg === 'string'
          ? msg
          : JSON.stringify(msg) || 'Ocurrió un error al procesar la solicitud'
      );
    }
  };

  return (
    <div
      className="modal fade show d-block"
      ref={modalRef}
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      data-bs-backdrop="static"
    >
      <div className="modal-dialog modal-dialog-centered mw-1000px">
        <div className="modal-content shadow-lg border-0">
          <div className="modal-header justify-content-end border-0 pb-0 pt-4 px-6">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-light-danger rounded-circle"
              onClick={closeModal}
            >
              <i className="ki-duotone ki-cross fs-2">
                <span className="path1" />
                <span className="path2" />
              </i>
            </button>
          </div>

          <form
            id={`cpd-${kind}-form`}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit((data) => submit(data, `${isEdit ? 'edit' : 'add'}-cpd-${kind}`))(e);
            }}
          >
            <div className="modal-body pt-0 pb-12 px-6 px-xl-15">
              <div className="text-center mb-8">
                <h1 className="mb-2 fs-2x">{isEdit ? meta.title : meta.titleNew}</h1>
                <p className="text-muted fw-semibold fs-6">
                  {kind === 'operator' &&
                    'Política CPD por operador (cliente). Gana sobre la ruta; pierde ante el override operador × ruta.'}
                  {kind === 'route' &&
                    'Política CPD por grupo de ruta de OpenSIPS (dr_gateways.group_id / carrier).'}
                  {kind === 'route-operator' &&
                    'Capa más específica: aplica solo cuando coinciden operador y grupo de ruta.'}
                </p>
                <div className="separator border-2 my-4"></div>
              </div>

              <div className="row g-7">
                <div className="col-lg-6">
                  <Title
                    title="Datos generales"
                    subtitle="Identidad y template base"
                    icon="ki-notepad-edit"
                  />

                  {meta.hasOperator && (
                    <div className="fv-row mb-5">
                      <Input
                        label="ID Operador"
                        requiredIndicator="required"
                        placeholder="Ej. coppel, banorte…"
                        errors={errors.operatorId}
                        {...register('operatorId', {
                          required: 'El operatorId es requerido',
                          maxLength: { value: 80, message: 'Máximo 80 caracteres' },
                          setValueAs: (v) => (typeof v === 'string' ? v.trim() : v)
                        })}
                      />
                    </div>
                  )}

                  {meta.hasRoute && (
                    <div className="fv-row mb-5">
                      <Input
                        label="Grupo de ruta (routeGroupId)"
                        requiredIndicator="required"
                        type="number"
                        placeholder="10 TELCEL · 20 ATT · 30 MOVISTAR · 40 ALTAN · 99 onering"
                        errors={errors.routeGroupId}
                        {...register('routeGroupId', {
                          required: 'El routeGroupId es requerido',
                          validate: (v) => {
                            const n = Number(v);
                            if (!Number.isInteger(n) || n < 0) return 'Debe ser un entero ≥ 0';
                            if (n > 4_000_000_000) return 'Fuera de rango';
                            return true;
                          }
                        })}
                      />
                    </div>
                  )}

                  {meta.hasCarrier && (
                    <div className="fv-row mb-5">
                      <Input
                        label="Carrier"
                        optional
                        placeholder="TELCEL, ATT, MOVISTAR, ALTAN…"
                        errors={errors.carrier}
                        {...register('carrier', {
                          maxLength: { value: 40, message: 'Máximo 40 caracteres' }
                        })}
                      />
                    </div>
                  )}

                  <div className="fv-row mb-5">
                    <label className="form-label">Template base</label>
                    <Controller
                      control={control}
                      name="templateId"
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <>
                          <Select
                            options={templateOptions}
                            isLoading={loadingTemplates}
                            isSearchable
                            className={
                              error ? 'form-select-custom is-invalid' : 'form-select-custom'
                            }
                            classNamePrefix="form-select-custom"
                            onChange={(opt: any) => onChange(opt ? opt.value : '')}
                            value={
                              templateOptions.find((o) => o.value === (value ?? '')) ?? NO_TEMPLATE
                            }
                            placeholder="Selecciona un template…"
                          />
                          <InvalidFeedback error={error} />
                        </>
                      )}
                    />
                  </div>

                  <div className="fv-row mb-5">
                    <label className="form-label">Descripción</label>
                    <TexArea
                      rows={3}
                      placeholder="Para qué existe este registro…"
                      {...register('description', {
                        maxLength: { value: 255, message: 'Máximo 255 caracteres' }
                      })}
                    />
                    <InvalidFeedback error={errors.description} />
                  </div>

                  <div className="fv-row mb-5">
                    <label className="form-check form-switch form-check-custom form-check-solid gap-3 style-pointer">
                      <input
                        className="form-check-input w-45px h-25px"
                        type="checkbox"
                        {...register('enabled')}
                      />
                      <span className="form-check-label fw-bold text-gray-700 fs-7">
                        Habilitado
                      </span>
                    </label>
                    <div className="form-text">
                      Deshabilitado = la capa se ignora al resolver la política (no se borra).
                    </div>
                  </div>

                  <div
                    className={`alert d-flex align-items-center p-4 mb-0 ${
                      overridesCount > 0 ? 'alert-warning' : 'alert-secondary'
                    }`}
                  >
                    <Icon
                      icon={overridesCount > 0 ? 'ki-information-5' : 'ki-check-circle'}
                      size="fs-2 me-3"
                    />
                    <span className="fs-7">
                      {overridesCount > 0
                        ? `${overridesCount} parámetro(s) forzado(s) sobre el template.`
                        : 'Sin parámetros forzados: hereda todo del template/capa anterior.'}
                    </span>
                  </div>
                </div>

                <div className="col-lg-6">
                  <CpdOverridesFields
                    control={control}
                    register={register}
                    errors={errors}
                    watch={watch}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-center align-items-center pt-10 gap-3">
                <button
                  type="button"
                  className="btn btn-light hover-scale fs-7 fw-bold"
                  onClick={closeModal}
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  id={`send-cpd-${kind}`}
                  className="btn btn-primary hover-scale fs-7 fw-bold"
                  disabled={!isValid}
                  isLoading={isSubmitting}
                >
                  {isEdit ? 'Guardar cambios' : 'Crear registro'}
                  <Icon icon="ki-send" size="fs-4" className="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
